import { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Form, Input, Button, Select, Switch, Space, Spin, message,
  Card, Table, InputNumber, Popconfirm, Typography,
} from 'antd';
import { PlusOutlined, DeleteOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import { useTranslation } from 'react-i18next';
import {
  getDashboard, createDashboard, updateDashboard,
  type CreateWidgetDto,
} from '@/api/dashboardApi';
import { useAuth } from '@/hooks/useAuth';

const { TextArea } = Input;

const WIDGET_TYPES = [
  'metric_card', 'bar_chart', 'pie_chart', 'line_chart', 'funnel', 'table', 'activity_feed',
] as const;

const DATA_SOURCES = [
  'sales_pipeline_summary', 'revenue_overview', 'customer_analytics',
  'activity_summary', 'pipeline_funnel', 'top_performers',
] as const;

const LAYOUT_OPTIONS = ['grid', 'freeform'] as const;

interface WidgetRow {
  key: string;
  type: string;
  title: string;
  data_source: string;
  pos_x: number;
  pos_y: number;
  width: number;
  height: number;
}

export default function DashboardForm() {
  const { id } = useParams<{ id: string }>();
  const isEdit = !!id;
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { hasPermission } = useAuth();
  const [form] = Form.useForm();

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [widgets, setWidgets] = useState<WidgetRow[]>([]);
  const [nextKey, setNextKey] = useState(1);

  const canWrite = hasPermission('dashboards', 'write');

  // Load dashboard for edit
  useEffect(() => {
    if (!isEdit) return;
    setLoading(true);
    getDashboard(id)
      .then((res) => {
        if (res.data) {
          const d = res.data;
          form.setFieldsValue({
            name: d.name,
            description: d.description || undefined,
            layout: d.layout || 'grid',
            is_default: d.is_default,
          });
          setWidgets(
            (d.widgets ?? []).map((w, i) => ({
              key: `existing_${i}`,
              type: w.type,
              title: w.title,
              data_source: w.data_source,
              pos_x: w.position?.x ?? 0,
              pos_y: w.position?.y ?? i,
              width: w.size?.width ?? 8,
              height: w.size?.height ?? 4,
            }))
          );
          setNextKey((d.widgets?.length ?? 0) + 1);
        }
      })
      .finally(() => setLoading(false));
  }, [id, isEdit, form]);

  const addWidget = useCallback(() => {
    setWidgets((prev) => [
      ...prev,
      {
        key: `new_${nextKey}`,
        type: 'metric_card',
        title: '',
        data_source: 'sales_pipeline_summary',
        pos_x: 0,
        pos_y: prev.length,
        width: 8,
        height: 4,
      },
    ]);
    setNextKey((n) => n + 1);
  }, [nextKey]);

  const updateWidget = useCallback(
    (key: string, field: keyof WidgetRow, value: unknown) => {
      setWidgets((prev) =>
        prev.map((w) => (w.key === key ? { ...w, [field]: value } : w))
      );
    },
    []
  );

  const removeWidget = useCallback(
    (key: string) => setWidgets((prev) => prev.filter((w) => w.key !== key)),
    []
  );

  const handleSubmit = useCallback(
    async (values: Record<string, unknown>) => {
      setSaving(true);
      try {
        const widgetDtos: CreateWidgetDto[] = widgets.map((w) => ({
          type: w.type,
          title: w.title,
          data_source: w.data_source,
          position: { x: w.pos_x, y: w.pos_y },
          size: { width: w.width, height: w.height },
        }));

        const payload = {
          name: values.name as string,
          description: (values.description as string) || undefined,
          layout: (values.layout as string) || 'grid',
          is_default: (values.is_default as boolean) || false,
          widgets: widgetDtos,
        };

        if (isEdit) {
          await updateDashboard(id, payload);
          message.success(t('dashboards.update_success'));
          navigate(`/dashboards/${id}`);
        } else {
          const res = await createDashboard(payload);
          message.success(t('dashboards.create_success'));
          navigate(`/dashboards/${res.data?.id}`);
        }
      } catch (err) {
        message.error(err instanceof Error ? err.message : t('app.error'));
      } finally {
        setSaving(false);
      }
    },
    [isEdit, id, widgets, navigate, t]
  );

  useEffect(() => {
    if (!canWrite) navigate('/dashboards');
  }, [canWrite, navigate]);

  if (!canWrite) return null;
  if (loading) return <Spin size="large" />;

  const widgetColumns: ColumnsType<WidgetRow> = [
    {
      title: t('dashboards.widget_type_label'),
      dataIndex: 'type',
      key: 'type',
      width: 140,
      render: (val: string, record: WidgetRow) => (
        <Select
          value={val}
          style={{ width: '100%' }}
          onChange={(v) => updateWidget(record.key, 'type', v)}
          options={WIDGET_TYPES.map((wt) => ({
            value: wt,
            label: t(`dashboards.widget_type.${wt}`),
          }))}
        />
      ),
    },
    {
      title: t('dashboards.field.name'),
      dataIndex: 'title',
      key: 'title',
      render: (val: string, record: WidgetRow) => (
        <Input
          value={val}
          onChange={(e) => updateWidget(record.key, 'title', e.target.value)}
          placeholder={t('dashboards.field.name')}
        />
      ),
    },
    {
      title: t('dashboards.widget_data_source'),
      dataIndex: 'data_source',
      key: 'data_source',
      width: 200,
      render: (val: string, record: WidgetRow) => (
        <Select
          value={val}
          style={{ width: '100%' }}
          onChange={(v) => updateWidget(record.key, 'data_source', v)}
          options={DATA_SOURCES.map((ds) => ({
            value: ds,
            label: ds.replace(/_/g, ' '),
          }))}
        />
      ),
    },
    {
      title: t('dashboards.widget_width'),
      dataIndex: 'width',
      key: 'width',
      width: 70,
      render: (val: number, record: WidgetRow) => (
        <InputNumber
          value={val}
          min={4}
          max={24}
          style={{ width: '100%' }}
          onChange={(v) => updateWidget(record.key, 'width', v ?? 8)}
        />
      ),
    },
    {
      title: t('dashboards.widget_height'),
      dataIndex: 'height',
      key: 'height',
      width: 70,
      render: (val: number, record: WidgetRow) => (
        <InputNumber
          value={val}
          min={2}
          max={12}
          style={{ width: '100%' }}
          onChange={(v) => updateWidget(record.key, 'height', v ?? 4)}
        />
      ),
    },
    {
      title: '',
      key: 'actions',
      width: 50,
      render: (_: unknown, record: WidgetRow) => (
        <Popconfirm
          title={t('app.confirm')}
          onConfirm={() => removeWidget(record.key)}
          okText={t('app.yes')}
          cancelText={t('app.no')}
        >
          <Button type="link" danger icon={<DeleteOutlined />} size="small" />
        </Popconfirm>
      ),
    },
  ];

  return (
    <div>
      <Typography.Title level={4}>
        {t(isEdit ? 'dashboards.edit_title' : 'dashboards.create_title')}
      </Typography.Title>

      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
        initialValues={{ layout: 'grid', is_default: false }}
      >
        <Form.Item
          name="name"
          label={t('dashboards.field.name')}
          rules={[{ required: true, message: t('dashboards.validation_name_required') }]}
        >
          <Input maxLength={200} />
        </Form.Item>

        <Form.Item name="description" label={t('dashboards.field.description')}>
          <TextArea rows={2} maxLength={2000} />
        </Form.Item>

        <Space size="large">
          <Form.Item name="layout" label={t('dashboards.field.layout')}>
            <Select
              style={{ width: 160 }}
              options={LAYOUT_OPTIONS.map((l) => ({
                value: l,
                label: t(`dashboards.layout_${l}`),
              }))}
            />
          </Form.Item>
          <Form.Item name="is_default" label={t('dashboards.field.is_default')} valuePropName="checked">
            <Switch />
          </Form.Item>
        </Space>

        <Card
          title={t('dashboards.field.widgets')}
          extra={
            <Button type="dashed" icon={<PlusOutlined />} onClick={addWidget}>
              {t('dashboards.add_widget')}
            </Button>
          }
          style={{ marginBottom: 16 }}
        >
          <Table<WidgetRow>
            dataSource={widgets}
            columns={widgetColumns}
            rowKey="key"
            pagination={false}
            size="small"
          />
        </Card>

        <Form.Item>
          <Space>
            <Button type="primary" htmlType="submit" loading={saving}>
              {t('app.save')}
            </Button>
            <Button onClick={() => navigate(isEdit ? `/dashboards/${id}` : '/dashboards')}>
              {t('app.cancel')}
            </Button>
          </Space>
        </Form.Item>
      </Form>
    </div>
  );
}
